import { PlusOutlined } from "@ant-design/icons"
import {
  Button,
  Card,
  Drawer,
  Form,
  Input,
  List,
  message,
  Modal,
  Select,
  Switch,
  Table,
  Tag,
  Typography
} from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  CreateCompanyRequest,
  GetCompanyUsersByCompanyIdRequest,
  GetFeatureFlagsByCompanyIdRequest,
  UpdateCompanyRequest,
  UpdateFeatureFlagStatusByCompanyIdRequest,
} from "../../../constants/apiRequestResponse"
import {
  Company,
  CompanySizes,
  CompanyUser,
  FeatureFlag,
  FeatureFlagStatus,
} from "../../../constants/types"
import {
  createCompany,
  deleteCompany,
  getAllCompanies,
  getCompanyUsersByCompanyId,
  getFeatureFlagsByCompanyId,
  updateCompany,
  updateFeatureFlagStatusByCompanyId
} from "../../../services/api"
import { AdminCompanyTabKeys } from "../../../services/i8tn/AdminCompanyTab/keys"
import { getQueryParam, parseTime, parseTimeAgo } from "../../../util"

export default function CompanyTab() {
  const { t } = useTranslation()

  enum DrawerModes {
    closed,
    add,
    edit,
  }

  const [isLoading, setIsLoading] = useState(false)
  const [isEditOrAddLoading, setIsEditOrAddLoading] = useState(false)
  const [companies, setCompanies] = useState<Company[] | undefined>()
  const [drawerMode, setDrawerMode] = useState<DrawerModes>(DrawerModes.closed)
  const [companyUsersByCompanyId, setCompanyUsersByCompanyId] = useState<{
    [key: string]: CompanyUser[] | undefined
  }>()
  const [featureFlagsByCompanyId, setFeatureFlagsByCompanyId] = useState<{
    [key: string]: FeatureFlag[] | undefined
  }>()
  const [
    isFeatureFlagLoadingByFeatureFlagTemplateId,
    setIsFeatureFlagLoadingByFeatureFlagTemplateId,
  ] = useState<{
    [key: string]: boolean
  }>({})
  const [companyLogoBase64, setCompanyLogoBase64] = useState<string | undefined>()
  const companyId = getQueryParam("companyId")

  const isDrawerOpen = drawerMode !== DrawerModes.closed

  const [form] = Form.useForm<{
    _id: string,
    logo: string,
    logoFile: File,
    name: string,
    subDomain: string,
    isAdmin: boolean,
    emailDomains: string[],
    helpEmail: string,
    industry: string,
    location: string,
    remark: string,
    size: CompanySizes,
  }>()

  const getUrlFromCompany = (companyOrSubdomain?: Company): string => {
    const subDomain = companyOrSubdomain?.subDomain
    const url = window.location.origin
    const subDomainWithProtocol = url.split(".")[0]
    const mainDomain = url.replace(subDomainWithProtocol, "")

    return `${window.location.protocol}//${subDomain}${mainDomain}`
  }

  const columns = [
    {
      title: "Logo",
      dataIndex: "logo",
      render: (logo) => (
        <img src={logo} alt={logo} loading="lazy" height={50} />
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (name: string, company: Company) => {
        return (
          <div style={{ display: 'flex', flexDirection: 'row', gap: 10 }}>
            <p>{name}</p>
            {
              company?.isAdmin && <Tag color="blue">admin</Tag>
            }
          </div>
        )
      }
    },
    {
      title: "Url",
      render: (company: Company) => (
        <a target="__blank" href={getUrlFromCompany(company)}>
          {getUrlFromCompany(company)}
        </a>
      ),
    },
    {
      title: "Industry",
      dataIndex: "industry",
    },
    {
      title: "Size",
      dataIndex: "size",
    },
    {
      title: "Location",
      dataIndex: "location",
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      render: (time) => parseTime(time),
    },
    {
      title: "Action",
      render: (company: Company) => {
        return (
          <div>
            <Button
              type="link"
              data-testclass={`edit-company-btn`}
              onClick={() => {
                setDrawerMode(DrawerModes.edit)

                form.setFieldsValue(company)
              }}
            >
              Edit
            </Button>
            {!company.isAdmin && (
              <Button
                type="link"
                danger
                data-testclass={`delete-company-btn`}
                onClick={() => {
                  Modal.confirm({
                    title: t(AdminCompanyTabKeys.adminCompanyTab_deleteCompanyTitle),
                    content: t(AdminCompanyTabKeys.adminCompanyTab_deleteCompanyContent),
                    okText: t(AdminCompanyTabKeys.adminCompanyTab_deleteBtn),
                    okType: "danger",
                    onOk() {
                      return deleteCompany(company._id)
                        .then(() => {
                          message.success(`${t(AdminCompanyTabKeys.adminCompanyTab_deletedSuccessMessage)} "${company.name}"`)
                          getData()
                        })
                        .catch((err) => {
                          message.error(err.toString())
                        })
                    },
                  })
                }}
              >
                {t(AdminCompanyTabKeys.adminCompanyTab_deleteBtn)}
              </Button>
            )}
          </div>
        )
      },
    },
  ]

  useEffect(() => {
    getData()
  }, [companyId])

  const getData = () => {
    setIsLoading(true)
    getAllCompanies()
      .then((res) => {
        if (!res.success) throw new Error(res.message)
        setCompanies(res.companies)
      })
      .catch((err) => {
        message.error(err.toString())
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  return (
    <div>
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <Typography.Title level={4}>Company</Typography.Title>
        <Button
          type="primary"
          onClick={() => {
            setDrawerMode(DrawerModes.add)
          }}
          data-testid="create-company-btn"
          icon={<PlusOutlined />}
        >
          Create
        </Button>
      </div>
      <Table
        loading={isLoading}
        rowKey={"_id"}
        columns={columns}
        scroll={{
          x: columns.length * 150
        }}
        dataSource={companies?.filter(c => !c.isDeleted)}
        expandable={{
          rowExpandable: (company: Company) => {
            return !company.isAdmin
          },
          onExpand: (expanded: boolean, company: Company) => {
            if (!expanded) return null

            const req: GetCompanyUsersByCompanyIdRequest = {
              companyId: company._id,
            }
            getCompanyUsersByCompanyId(req)
              .then((res) => {
                if (!res.success) throw new Error(res.message)
                setCompanyUsersByCompanyId({
                  ...companyUsersByCompanyId,
                  [company._id]: res.companyUsers,
                })
              })
              .catch((err) => {
                message.error(err.toString())
              })

            const req2: GetFeatureFlagsByCompanyIdRequest = {
              companyId: company._id,
            }
            getFeatureFlagsByCompanyId(req2)
              .then((res) => {
                if (!res.success) throw new Error(res.message)
                setFeatureFlagsByCompanyId((prev) => ({
                  ...prev,
                  [company._id]: res.featureFlags,
                }))
              })
              .catch((err) => {
                message.error(err.toString())
              })
          },
          expandedRowRender: (company: Company) => {
            return (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  width: window.innerWidth * 0.9,
                  gap: 10,
                }}
              >
                {!company.isAdmin ? (
                  <Card style={{ flex: 4 }}>
                    <Typography.Title level={5}>Feature flags</Typography.Title>

                    {featureFlagsByCompanyId ? (
                      <List
                        itemLayout="horizontal"
                        dataSource={featureFlagsByCompanyId[company._id]}
                        renderItem={(ff) => (
                          <List.Item
                            actions={[
                              <Switch
                                checked={
                                  ff.status === FeatureFlagStatus.ENABLED
                                }
                                loading={
                                  isFeatureFlagLoadingByFeatureFlagTemplateId[
                                  ff.featureFlagTemplateId
                                  ] || false
                                }
                                onChange={(toggleToValue) => {
                                  setIsFeatureFlagLoadingByFeatureFlagTemplateId(
                                    (prev) => ({
                                      ...prev,
                                      [ff.featureFlagTemplateId]: true,
                                    })
                                  )

                                  const toStatus =
                                    toggleToValue === true
                                      ? FeatureFlagStatus.ENABLED
                                      : FeatureFlagStatus.DISABLED
                                  const req: UpdateFeatureFlagStatusByCompanyIdRequest =
                                  {
                                    companyId: ff.companyId,
                                    featureFlagTemplateId:
                                      ff.featureFlagTemplateId,
                                    status: toStatus,
                                  }
                                  updateFeatureFlagStatusByCompanyId(req)
                                    .then((res) => {
                                      if (!res.success)
                                        throw new Error(res.message)

                                      const newFeatureFlagsOfCompany =
                                        featureFlagsByCompanyId[
                                          company._id
                                        ]?.map((v) => {
                                          if (v._id === ff._id) {
                                            v.status = toStatus
                                            v.statusUpdatedAt = Date.now()
                                          }
                                          return v
                                        })

                                      setFeatureFlagsByCompanyId((prev) => ({
                                        ...prev,
                                        [company._id]: newFeatureFlagsOfCompany,
                                      }))

                                      message.success("Toggled success")
                                    })
                                    .catch((err) => {
                                      message.error(err.toString())
                                    })
                                    .finally(() => {
                                      setIsFeatureFlagLoadingByFeatureFlagTemplateId(
                                        (prev) => ({
                                          ...prev,
                                          [ff.featureFlagTemplateId]: false,
                                        })
                                      )
                                    })
                                }}
                              />,
                            ]}
                          >
                            <List.Item.Meta
                              title={ff.name}
                              description={<div dangerouslySetInnerHTML={{ __html: ff.description }}>
                              </div>}
                            />
                            <div>
                              <Typography.Text>
                                Last Toggled:{" "}
                                {ff.statusUpdatedAt
                                  ? parseTimeAgo(ff.statusUpdatedAt)
                                  : "Never"}
                              </Typography.Text>
                            </div>
                          </List.Item>
                        )}
                      />
                    ) : (
                      <List.Item.Meta description={"No feature flags found"} />
                    )}
                  </Card>
                ) : null}
              </div>
            )
          },
        }}
      />

      <Drawer
        title={
          drawerMode === DrawerModes.add ? "Add a new company" : `Edit company`
        }
        placement="right"
        visible={isDrawerOpen}
        onClose={() => {
          setDrawerMode(DrawerModes.closed)
          setCompanyLogoBase64(undefined)
          form.resetFields()
        }}
        width={300}
        destroyOnClose={true}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={async (formValues) => {
            try {
              setIsEditOrAddLoading(true)

              if (drawerMode === DrawerModes.edit) {
                if (!formValues._id) return

                const req2: UpdateCompanyRequest = formValues
                await updateCompany(req2, formValues._id)
                message.success(`Updated successfully`)
              } else {
                const req2: CreateCompanyRequest = formValues
                await createCompany(req2)
                message.success(`Created successfully`)
              }

              setDrawerMode(DrawerModes.closed)
              getData()
              setCompanyLogoBase64(undefined)
              form.resetFields()

            } catch (err) {
              message.error(err?.toString())
            }

            setIsEditOrAddLoading(false)
          }}
        >
          <Form.Item
            name="_id"
            hidden
          >
          </Form.Item>
          <Form.Item
            name="logoBase64"
            hidden
          >
          </Form.Item>
          <Form.Item label="Logo" name="logoFile">
            <img alt="" src={companyLogoBase64 || form.getFieldValue('logo')} width={200} />
            <input
              type="file"
              name="img"
              accept="image/*"
              onChange={async (e) => {
                const file = e.target.files && e.target.files[0]
                if (!file) {
                  throw new Error(`Error attaching file`)
                }
                form.setFieldsValue({ 'logoFile': file })

                // convert to base64 & set state for viewing purposes only
                var reader = new FileReader()
                if (file) {
                  reader.readAsDataURL(file)

                  reader.onload = async function () {
                    const _base64 = reader.result as string
                    if (!_base64) return

                    setCompanyLogoBase64(_base64)
                  }
                  reader.onerror = function (error) {
                    console.log("Error: ", error)
                  }
                }
              }}
            />
          </Form.Item>

          <Form.Item
            label="Name"
            name="name"
            rules={[
              {
                required: true,
                message: "Name is required",
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={'Subdomain'}
            name="subDomain"
            rules={[
              {
                required: true,
                message: "Subdomain is required",
              },
            ]}
          >
            <Input />
          </Form.Item>
          <br />
          <Form.Item
            extra={"E.g. gmail.com, acme.com, thedevelopmentcentre.com."}
            label={"Email domains"}
            name="emailDomains"
            rules={[
              {
                required: true,
                message: "Email domains is required",
              },
            ]}
          >
            <Select mode="tags" style={{ width: "100%" }}></Select>
          </Form.Item>

          <Form.Item
            extra="Email for users to reach out to incase something went wrong."
            label='Help desk email'
            name="helpEmail"
            rules={[
              {
                type: "email",
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="Industry" name="industry">
            <Input />
          </Form.Item>
          <Form.Item label="Company Size" name="size">
            <Select
              onChange={v => {
            /**
             * if we clear this option, the value will be undefined
             * this leads to us not being able to distinguish whether we want to
             * set value to empty
             * or
             * do not update this field
             * when we call the PUT api
             * 
             * as such extra handling is required here 
             */
                if (v === undefined) {
                  form.setFieldsValue({
                    size: CompanySizes.empty_value
                  })
                }
              }}
              allowClear showSearch placeholder={CompanySizes.medium}
            >
              {[
                CompanySizes.enterprise,
                CompanySizes.large,
                CompanySizes.medium,
                CompanySizes.small,
                CompanySizes.micro,
              ].map((size) => {
                return (
                  <Select.Option key={size} value={size}>
                    {size}
                  </Select.Option>
                )
              })}
            </Select>
          </Form.Item>

          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Form.Item>
              {drawerMode === DrawerModes.edit ? (
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isEditOrAddLoading}
                >
                  Save
                </Button>
              ) : (
                  <Button
                  type="primary"
                    htmlType="submit"
                  loading={isEditOrAddLoading}
                >
                  Add
                </Button>
              )}
            </Form.Item>
          </div>
        </Form>
      </Drawer>
    </div>
  )
}
